import React from "react";
import { Search, X, Filter } from "lucide-react";
import { AgentFilterOption } from "../../types/agent";

interface AgentSearchBarProps {
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  category: string;
  onCategoryChange: (category: string) => void;
  onClearAllFilters: () => void;
  categoryOptions: AgentFilterOption[];
  totalCount: number;
  debouncedSearchTerm: string;
  placeholder?: string;
}

export default function AgentSearchBar({
  searchTerm,
  onSearchChange,
  onKeyPress,
  onClearSearch,
  category,
  onCategoryChange,
  onClearAllFilters,
  categoryOptions,
  totalCount,
  debouncedSearchTerm,
  placeholder = "에이전트 제목으로 검색..."
}: AgentSearchBarProps) {
  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder={placeholder}
              value={searchTerm}
              onChange={onSearchChange}
              onKeyPress={onKeyPress}
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            />
            {searchTerm && (
              <X
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5 cursor-pointer"
                onClick={onClearSearch}
              />
            )}
          </div>
        </div>

        {/* 카테고리 필터 */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative min-w-[180px]">
            <select
              value={category}
              onChange={(e) => onCategoryChange(e.target.value)}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer"
            >
              {categoryOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  className="bg-slate-800"
                >
                  {option.label}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <svg
                className="w-4 h-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {(searchTerm || category) && (
            <button
              onClick={onClearAllFilters}
              className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors flex items-center gap-2 min-w-[120px] justify-center"
            >
              <Filter className="w-4 h-4" />
              초기화
            </button>
          )}
        </div>
      </div>

      <div className="mt-4">
        <p className="text-slate-400 text-sm">
          총{" "}
          <span className="text-white font-medium">{totalCount}</span>
          개의 에이전트
          {debouncedSearchTerm && (
            <span className="ml-2 text-purple-400">
              ('{debouncedSearchTerm}' 검색 결과)
            </span>
          )}
          {category && (
            <span className="ml-2 text-blue-400">
              •{" "}
              {
                categoryOptions.find((opt) => opt.value === category)?.label
              }
            </span>
          )}
        </p>
      </div>
    </div>
  );
}